import { mainLogger as log } from "@/app/logger"
import {
  Category,
  Dict,
  Firm,
  Fund,
  Interval,
  List,
  TermsList,
} from "@/app/types"

// stores the values of various metrics, e.g. "aum": NNN, or "mktSharePcent": NNN
export type Values = Dict<number | string>

// represent periodic and ranking values for all terms
export class TermValues {
  periodic: List<Values>
  ranking: Dict<Values>
}

// represent the term metrics of a GroupMember
export class GroupMemberValues {
  // a group member is either a fund or firm
  fund?: Fund
  firm?: Firm
  daily?: Dict<TermValues>
  monthly?: Dict<TermValues>
  quarterly?: Dict<TermValues>
  annual?: Dict<TermValues>

  periodic?: List<Values>
  ranking?: Dict<Values>

  filterIncluded: boolean
  rank?: number | string // custom property

  // periodic and ranking values from TermValues
  // for this group member fund/firm
}

export class GroupValues {
  key: string
  // metrics for the group as a whole
  AVG: TermValues
  MAX: TermValues
  MED: TermValues
  MIN: TermValues
  SUM: TermValues
  P25: TermValues

  // metrics for indivdual members of the group (as a list)
  members: List<GroupMemberValues>

  filterCount: number
}

// list of top10 members (funds or firms) along with their member values
export type Top10List = GroupMemberValues[] // expected to have 10 values

// the Top10 funds of the category or Top10 firms of the industry
// Note that the Top10List changes by Metric.name and also by Ranking.term
// so for each metric (e.g. by AUM) we have,
//  - one Top10List for periodic => Top10Values.members.periodic
//  - one Top10List for each ranking term => Top10Values.members.ranking[term.key]
export class Top10Values {
  key: string
  // metrics for the "top10" group as a whole
  AVG: TermValues
  MAX: TermValues
  MED: TermValues
  MIN: TermValues
  SUM: TermValues
  P25: TermValues

  // the top10 members are different for periodic vs ranking charts
  //  - for periodic charts, we sort by the values of the latest period to identify the top10
  //  - for ranking charts, we sort by values of the [Term.key], e.g. 10YR top10 is different than 5YR top10
  members: {
    // for periodic charts, we don't use the values of the individual members, just use fund/firm info
    // periodic charts only show the Entity values and the Top10 values (as a group, given above)
    periodic: Top10List
    ranking: Dict<Top10List> // [term.key] => Top10List
  }

  filterCount: number
}

export class PeriodicLabel {
  idx: number
  label: string
  year: number
  year_idx: number
}

export class EntitySets {
  peers: GroupValues
  customMetrics?: GroupValues
  industry?: GroupValues
  category?: GroupValues
}

export class EntityValues extends TermValues {
  // one of fund or firm
  firm?: Firm
  firmsets?: EntitySets

  fund?: Fund
  fundsets?: EntitySets

  labels: List<PeriodicLabel>
  // terms for which we have values of this Entity
  // same as the terms in /meta/terms but only one interval
  terms: TermsList

  // periodic and ranking values for this entity come from TermValues
}

// industry/category are a "group" of firms/funds respectively
// so they have the same structure as GroupValues plus additional properties
export class AllValues extends GroupValues {
  // the category for which this values (not present for industry)
  category?: Category
  firmsets?: EntitySets
  fundsets?: EntitySets
  // terms for which we have values of this Group
  // same as the terms in /meta/terms but only one interval
  terms: TermsList

  filter: string

  // mapping of Metric.name -> Top10Values (by that metric)
  top10: Dict<Top10Values>
}

export class ValuesAllIntervals<ValuesType extends EntityValues | AllValues> {
  daily: ValuesType
  monthly: ValuesType
  quarterly: ValuesType
  annual: ValuesType

  set(interval: string, data: ValuesType): void {
    if (!Interval.isValidInterval(interval)) {
      log.error(
        "Unexpected interval. Expected:",
        Interval.NAMES,
        "Got:",
        interval
      )
      return
    }
    // TODO - add more checks to the data
    this[interval] = data
  }

  get(interval: string): ValuesType | null {
    if (!Interval.isValidInterval(interval)) {
      log.error(
        "Unexpected interval. Expected:",
        Interval.NAMES,
        "Got:",
        interval
      )
      return null
    }
    return this[interval]
  }

  // check if a given interval is set, or check all if no interval is given
  isSet(interval: string | null = null): boolean {
    if (interval && !Interval.isValidInterval(interval)) {
      log.error(
        "Unexpected interval. Expected:",
        Interval.NAMES,
        "Got:",
        interval
      )
      return false
    }
    const toCheck = !interval ? Interval.NAMES : [interval]
    return toCheck.every((interval) => this[interval] !== null)
  }
}

// all intervals for industry
export class IndustryValues extends ValuesAllIntervals<AllValues> {}
